export type CuratedModelTag = "flagship" | "milestone";

/**
 * Open state of the Flagship, Milestone and Upcoming bars. App owns it because
 * the release list unmounts while "No models match" is shown, and the bars
 * must come back the way the reader left them.
 */
export interface CuratedSectionsOpen {
  flagship: boolean;
  milestone: boolean;
  upcoming: boolean;
}

export const DEFAULT_CURATED_SECTIONS_OPEN: CuratedSectionsOpen = {
  flagship: false,
  milestone: false,
  upcoming: true
};

export const FLAGSHIP_MODEL_IDS = [
  "openai-gpt-6-1-sol-b0dd27bc2e",
  "openai-gpt-6-astra-d5f76f414c",
  "openai-gpt-live-1-1b3294a213",
  "openai-gpt-image-2-5-flare-cef97f2c85",
  "openai-gpt-image-2-5-sunburst-fd18dc8171",
  "anthropic-claude-fable-5-1-396e25e2c6",
  "anthropic-claude-sonnet-5-5-6db1e1f3ba",
  "anthropic-claude-opus-5-5-dc8b2f9c99",
  "anthropic-claude-haiku-5-5-aa9720c888",
  "xai-grok-4-7-3d25509181",
  "google-deepmind-gemini-4-argon-04e917e4e7",
  "google-deepmind-gemini-omni-flash-81c7615175",
  "bytedance-seedance-2-5-aa1de598ff",
  "minimax-minimax-h3-71ded17fda",
  "deepseek-deepseek-v4-1-flash-fa57285d53",
  "z-ai-glm-5-3-flash-3953c426b4",
  "xiaomi-mimo-v2-6-flash-3c263a59a8",
  "reve-reve-2-1-408ca5cfa3",
  "mistral-ai-mistral-large-4-83fcb567b8",
  "stepfun-step-5-preview-ad4cf8ffbc"
] as const;

export const MILESTONE_MODEL_IDS = [
  "openai-gpt-3-90814f24f1",
  "openai-gpt-4-aeebdaa6f5",
  "openai-gpt-5-04843cfbb4",
  "openai-o1-8ed746e2d1",
  "openai-gpt-4o-7b857c8ed8",
  "openai-gpt-image-2-a7932aac52",
  "openai-gpt-4o-realtime-preview-470e01f8bf",
  "openai-gpt-live-transcribe-895e162598",
  "anthropic-claude-mythos-5-064efbbad4",
  "anthropic-claude-opus-4-7ebe9936f3",
  "anthropic-claude-fable-5-77a3118a40",
  "moonshot-ai-kimi-k3-f1d161baf2",
  "deepseek-deepseek-r1-1b183f60e0",
  "google-deepmind-nano-banana-a8c4e085fd",
  "google-deepmind-gemini-omni-flash-81c7615175",
  "xiaomi-mimo-v2-5-pro-ultraspeed-79531c2b37",
  "bytedance-seedance-2-0-b6647856d3",
  "minimax-minimax-h3-71ded17fda",
  "typesafe-ai-jev-1-13-15bb144500"
] as const;

const FLAGSHIP_IDS = new Set<string>(FLAGSHIP_MODEL_IDS);
const MILESTONE_IDS = new Set<string>(MILESTONE_MODEL_IDS);

export function isFlagshipModel(modelId: string): boolean {
  return FLAGSHIP_IDS.has(modelId);
}

export function getCuratedModelTags(modelId: string): CuratedModelTag[] {
  const tags: CuratedModelTag[] = [];
  if (FLAGSHIP_IDS.has(modelId)) tags.push("flagship");
  if (MILESTONE_IDS.has(modelId)) tags.push("milestone");
  return tags;
}

export function curatedModelSearchTerms(modelId: string): string {
  return getCuratedModelTags(modelId)
    .flatMap((tag) =>
      tag === "flagship"
        ? ["flagship", "flagship model", "旗舰", "旗舰模型"]
        : ["milestone", "milestone model", "里程碑", "里程碑模型"]
    )
    .join(" ");
}
